import { ApplicationFeeStatus, AppliedDiscountInfo, Money } from "shared/types";
import { formatToCurrency } from "shared/utils";
import { formatTitleFirstWord } from "utils/formatTitle";
import { cx } from "utils/classnames";
import { Check, Minus, FlipBackward } from "untitledui-js-base";
import { Cell } from "./Cell";
import { ApplicantOrInvitee } from "../types";

type ExtendedDiscountInfo = AppliedDiscountInfo & {
  final_amount?: Money;
};

type ApplicantWithDiscount = ApplicantOrInvitee & {
  discount_info?: ExtendedDiscountInfo;
};

interface ApplicationFeeCellProps {
  applicant: ApplicantOrInvitee;
}

const iconMap = {
  [ApplicationFeeStatus.Paid]: (
    <Check size="20" className="stroke-success-600" />
  ),
  [ApplicationFeeStatus.Refunded]: (
    <FlipBackward size="20" className="stroke-warning-500" />
  ),
  [ApplicationFeeStatus.Unpaid]: <Minus className="stroke-content-200" />
};

export const ApplicationFeeCell = ({ applicant }: ApplicationFeeCellProps) => {
  const isApplicant = "first_name" in applicant;

  if (
    !isApplicant ||
    applicant.application_fee_status === ApplicationFeeStatus.NoApplicationFee
  )
    return (
      <Cell isFirst isDataCell>
        -
      </Cell>
    );

  const applicantWithDiscount = applicant as ApplicantWithDiscount;
  const unitChanged = applicant.audit_summary_flags.unit_id_changed || false;
  return (
    <Cell isFirst isDataCell>
      <div className="flex gap-2 items-center">
        {iconMap[applicant.application_fee_status]}
        <p
          className={cx(
            "font-medium",
            applicant.application_fee_status === ApplicationFeeStatus.Unpaid
              ? "text-content-200"
              : "text-content-1000"
          )}
        >
          {formatTitleFirstWord(applicant.application_fee_status)}
        </p>
        <p
          className={cx(
            applicant.application_fee_status === ApplicationFeeStatus.Unpaid
              ? "text-content-200"
              : "text-content-300"
          )}
        >
          {applicantWithDiscount.discount_info?.discount_applied ? (
            <>
              (
              <span className="line-through">
                {formatToCurrency(
                  // If paid and unit was changed, calculate original from paid + discount
                  applicant.application_fee_status ===
                    ApplicationFeeStatus.Paid &&
                    unitChanged &&
                    applicant.paid_application_fee &&
                    applicantWithDiscount.discount_info.discount_amount
                    ? {
                        cents:
                          applicant.paid_application_fee.cents +
                          (typeof applicantWithDiscount.discount_info
                            .discount_amount === "number"
                            ? applicantWithDiscount.discount_info
                                .discount_amount
                            : applicantWithDiscount.discount_info
                                .discount_amount.cents),
                        currency: applicant.paid_application_fee.currency
                      }
                    : // Otherwise use the application fee
                      applicant.application_fee,
                  {
                    minimumFractionDigits: 2
                  }
                )}
              </span>{" "}
              {applicantWithDiscount.discount_info.final_amount &&
                formatToCurrency(
                  applicantWithDiscount.discount_info.final_amount,
                  {
                    minimumFractionDigits: 2
                  }
                )}
              )
            </>
          ) : (
            <>
              (
              {formatToCurrency(
                applicant.application_fee_status === ApplicationFeeStatus.Unpaid
                  ? applicant.application_fee
                  : applicant.paid_application_fee
              )}
              )
            </>
          )}
        </p>
      </div>
    </Cell>
  );
};
